import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { getUserTechStackData, updateUserTechStack } from '../services/userTechnologyService';
import BackButton from '../components/BackButton';
import BBALoader from '../components/BBALoader';
import { useTechnologyEmbedded } from '../hooks/useTechnologyEmbedded';
import { resolveCoreToolLinks, selectionDisplayName } from '../utils/techStackCoreLinks';
import { buildAddedAppSelection, newAddedAppToolId, tierToolTypes, withAddedApps } from '../utils/techStackAddedApps';
import AddTechStackAppModal from '../components/techstack/AddTechStackAppModal';
import { DEFAULT_TIER_ORDER, applyTierMeta, compareTechSoftware, compareTechTypes, fetchTechTierMeta } from '../utils/techStackTiers';
import { FaLightbulb, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import PortalBackground from '../components/PortalBackground';

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client account's tech stack instead of their own.
const TechStackVisualizer = ({ embedded: embeddedProp, overrideCompanyId = null, overrideUserId = null }) => {
  const { darkMode } = useTheme();
  const embedded = useTechnologyEmbedded(embeddedProp);
  const { currentUser } = useAuth();
  const { companyId: resolvedCompanyId, loading: resolvedCompanyIdLoading } = useCompanyId();
  const companyId = overrideCompanyId || resolvedCompanyId;
  const companyIdLoading = overrideCompanyId ? false : resolvedCompanyIdLoading;
  // The uid used for legacy per-user data (e.g. seeding an account's stack).
  const effectiveUid = overrideUserId || currentUser?.uid;
  const [activeLayer, setActiveLayer] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [tierOrder, setTierOrder] = useState(DEFAULT_TIER_ORDER);
  const [userTechStack, setUserTechStack] = useState([]);
  const [showDuplicateAnalysis, setShowDuplicateAnalysis] = useState(false);
  const [duplicateAnalysis, setDuplicateAnalysis] = useState([]);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [proposedTechStack, setProposedTechStack] = useState([]);
  const [showProposedList, setShowProposedList] = useState(false);
  const [customSoftware, setCustomSoftware] = useState({
    name: '',
    open: false
  });
  const [showSignupLinks, setShowSignupLinks] = useState(false);
  // Tier "+" — { mode: 'add'|'edit', categoryId, toolId?, initial? }
  const [appModal, setAppModal] = useState(null);
  const [appSaving, setAppSaving] = useState(false);

  // Define the tech stack structure with empty initial state
  const [techStack, setTechStack] = useState({
    core: {
      id: 'core',
      title: 'Core',
      color: 'bg-red-700',
      width: '40%',
      tools: []
    },
    primary: {
      id: 'primary',
      title: 'Primary',
      color: 'bg-red-600',
      width: '70%',
      tools: []
    },
    secondary: {
      id: 'secondary',
      title: 'Secondary',
      color: 'bg-red-500',
      width: '100%',
      tools: []
    }
  });

  // Fetch data from Firebase with real-time updates. isStale lets an unmounted
  // or superseded load (e.g. companyId resolved mid-flight) drop its result
  // instead of overwriting fresher state.
  const fetchTechStackData = async (userData = null, proposedData = null, isStale = () => false) => {
    try {
      const collectionNames = ['Core', 'Primary', 'Secondary'];
      const tierMeta = await fetchTechTierMeta();
      let newTechStack = { ...techStack };
      // Core tool types by id, so Primary/Secondary types can offer "your <Core tool>"
      const coreTypesById = new Map();
      
      // Use provided user data or current userTechStack state
      const currentUserData = userData || userTechStack;
      const currentProposedData = proposedData !== null ? proposedData : proposedTechStack;
      console.log('fetchTechStackData - currentUserData:', currentUserData);
      console.log('fetchTechStackData - currentProposedData:', currentProposedData);

      for (const collectionName of collectionNames) {
        const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
        const tools = [];

        for (const typeDoc of typesSnapshot.docs) {
          const typeData = typeDoc.data();
          
          const softwareSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares'));
          const software = [];

                      softwareSnapshot.forEach(softwareDoc => {
              const data = softwareDoc.data();
            
            if (data && data.name) {
              // Convert old useCases format to new useCaseIds format
              const useCaseIds = data.useCaseIds || (data.useCases ? data.useCases.map((_, index) => index.toString()) : []);
              
              software.push({
                id: softwareDoc.id,
                name: data.name,
                useCaseIds: useCaseIds,
                image: data.image || '',
                signupUrl: data.signupUrl || '',
                suggested: data.suggested || false,
                open: data.open || false,
                order: data.order
              });
            }
          });

          if (collectionName === 'Core') {
            coreTypesById.set(typeDoc.id, { name: typeData.name, software });
          } else {
            software.push(...resolveCoreToolLinks(typeData, coreTypesById, currentUserData, software));
          }

          // Template order if set, else suggested first
          software.sort(compareTechSoftware);

          if (typeData && typeData.name) {
            // Check if user has a saved selection for this tool
            const savedSelection = currentUserData.find(item => 
              item.categoryId === collectionName.toLowerCase() && item.toolId === typeDoc.id
            );
            
            console.log(`Tool ${typeData.name} (${typeDoc.id}) - savedSelection:`, savedSelection);
            
            // Check if there's a proposed software for this tool (only if no saved selection)
            const proposedItem = !savedSelection ? currentProposedData.find(item => 
              item.categoryId === collectionName.toLowerCase() && 
              item.toolId === typeDoc.id &&
              item.status !== 'removed'
            ) : null;
            const proposedSoftware = proposedItem ? proposedItem.name : '';
            
            // If saved selection is custom software, include its open API status
            const customSoftwareOpen = savedSelection?.isCustom ? (savedSelection.open || false) : false;
            
            tools.push({
              id: typeDoc.id,
              name: typeData.name,
              order: typeData.order,
              software: selectionDisplayName(savedSelection, software), // Saved selection (a linked Core pick follows the Core tool) or empty
              description: typeData.description || '',
              useCases: typeData.useCases || [], // Add use cases from type
              availableSoftware: software,
              proposedSoftware: proposedSoftware, // Add proposed software
              customSoftwareOpen: customSoftwareOpen // Add custom software open API status if it's custom
            });
          }
        }

        // Convert collection name to lowercase for state key
        const stateKey = collectionName.toLowerCase();
        // Apps the client added with the tier's "+" sit beside their tool type; new types go last
        newTechStack[stateKey].tools = withAddedApps(tools.sort(compareTechTypes), currentUserData, stateKey);
      }
      newTechStack = applyTierMeta(newTechStack, tierMeta);

      console.log('Final tech stack with user selections:', newTechStack);
      if (isStale()) return;
      setTierOrder(tierMeta.map((t) => t.id));
      setTechStack(newTechStack);
      setIsLoaded(true);
    } catch (error) {
      console.error('Error fetching tech stack data:', error);
      if (isStale()) return;
      setIsLoaded(true); // Still set loaded to true to show UI even if there's an error
    }
  };



  // Set up real-time listener and fetch data
  useEffect(() => {
    // Wait for the company lookup to settle: loading with companyId=null would
    // read (and later save) the per-user fallback doc for a company user,
    // which is how per-company stacks diverged.
    if (!currentUser || companyIdLoading) return;

    let cancelled = false;
    const isStale = () => cancelled;

    // Load selections + proposed list, then the stack templates
    const loadData = async () => {
      const userTechData = await getUserTechStackData(effectiveUid, companyId || null);
      const userSelections = userTechData.selectedSoftware || [];
      console.log('Loaded user selections:', userSelections);
      if (cancelled) return;
      setUserTechStack(userSelections);

      // Load proposed tech stack from company/user document
      let proposedSoftware = [];
      try {
        const baseRef = companyId
          ? doc(db, 'accounts', companyId)
          : doc(db, 'users', currentUser.uid);
        const baseDoc = await getDoc(baseRef);
        if (baseDoc.exists()) {
          proposedSoftware = baseDoc.data().techStack?.proposedSoftware || [];
        }
      } catch (error) {
        console.error('Error loading proposed tech stack:', error);
      }
      if (cancelled) return;
      setProposedTechStack(proposedSoftware);

      await fetchTechStackData(userSelections, proposedSoftware, isStale);
    };

    // Re-run the full load whenever the stack templates change
    const unsubscribe = onSnapshot(
      collection(db, 'technologyStack'),
      () => {
        loadData();
      },
      (error) => {
        console.error('Error listening to tech stack changes:', error);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [currentUser, companyId, companyIdLoading, effectiveUid]);

  // Helper function to dismiss item from proposed list (user-initiated)
  const handleDismissProposedItem = async (item) => {
    if (!currentUser) return;
    
    try {
      const baseRef = companyId
        ? doc(db, 'accounts', companyId)
        : doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(baseRef);
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const currentProposedTechStack = userData.techStack?.proposedSoftware || [];
      
      // Remove the specific item from the proposed list
      const updatedProposedTechStack = currentProposedTechStack.filter(proposedItem => 
        !(proposedItem.categoryId === item.categoryId && 
          proposedItem.toolId === item.toolId && 
          proposedItem.name === item.name)
      );
      
      if (updatedProposedTechStack.length !== currentProposedTechStack.length) {
        setProposedTechStack(updatedProposedTechStack);
        
        // Update Firestore (company-wide when companyId exists)
        const baseRef = companyId
          ? doc(db, 'accounts', companyId)
          : doc(db, 'users', currentUser.uid);
        await updateDoc(baseRef, {
          'techStack.proposedSoftware': updatedProposedTechStack
        });
        
        // Reload tech stack data to update the pyramid display
        const userTechData = await getUserTechStackData(effectiveUid, companyId || null);
        const userSelections = userTechData.selectedSoftware || [];
        await fetchTechStackData(userSelections, updatedProposedTechStack);
        
        console.log('Dismissed from proposed list:', item);
      }
    } catch (error) {
      console.error('Error dismissing from proposed list:', error);
    }
  };

  // Helper function to remove item from proposed list
  const removeFromProposedList = async (categoryId, toolId, softwareName = null, checkStatus = null) => {
    if (!currentUser) return;
    
    try {
      const baseRef = companyId
        ? doc(db, 'accounts', companyId)
        : doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(baseRef);
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const currentProposedTechStack = userData.techStack?.proposedSoftware || [];
      
      // Remove items that match the category and tool
      // If softwareName is provided, only remove if it matches
      // If checkStatus is provided, only remove items with that status
      const updatedProposedTechStack = currentProposedTechStack.filter(item => {
        if (item.categoryId === categoryId && item.toolId === toolId) {
          if (softwareName) {
            // If checking status, only remove if status matches
            if (checkStatus !== null) {
              return !(item.name === softwareName && item.status === checkStatus);
            }
            // Otherwise remove if name matches (regardless of status)
            return item.name !== softwareName;
          }
          // If no softwareName, remove all items for this tool
          return false;
        }
        return true; // Keep items for other tools
      });
      
      if (updatedProposedTechStack.length !== currentProposedTechStack.length) {
        setProposedTechStack(updatedProposedTechStack);
        
        // Update Firestore (company-wide when companyId exists)
        const baseRef = companyId
          ? doc(db, 'accounts', companyId)
          : doc(db, 'users', currentUser.uid);
        await updateDoc(baseRef, {
          'techStack.proposedSoftware': updatedProposedTechStack
        });
        
        // Reload tech stack data to update the pyramid display
        const userTechData = await getUserTechStackData(effectiveUid, companyId || null);
        const userSelections = userTechData.selectedSoftware || [];
        await fetchTechStackData(userSelections, updatedProposedTechStack);
        
        console.log('Removed from proposed list:', { categoryId, toolId, softwareName, checkStatus });
      }
    } catch (error) {
      console.error('Error removing from proposed list:', error);
    }
  };

  const handleLayerClick = (layerId) => {
    setActiveLayer(layerId === activeLayer ? null : layerId);
    setActiveTool(null);
  };

  // Save the tier "+" modal: add a new app, or edit / remove one added earlier
  const saveAddedApp = async ({ name, toolName, baseToolId, open, library }) => {
    if (!appModal) return;
    const toolId = appModal.toolId || newAddedAppToolId();
    const entry = buildAddedAppSelection({ categoryId: appModal.categoryId, toolId, name, toolName, baseToolId, open, library });
    const updatedUserTechStack = [...userTechStack.filter(item => item.toolId !== toolId), entry];
    setAppSaving(true);
    try {
      await updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);
      setUserTechStack(updatedUserTechStack);
      await fetchTechStackData(updatedUserTechStack);
      setAppModal(null);
    } catch (error) {
      console.error('Error saving added app:', error);
      alert('Could not save that app. Please try again.');
    } finally {
      setAppSaving(false);
    }
  };

  const removeAddedApp = async () => {
    if (!appModal?.toolId || !window.confirm(`Remove ${appModal.initial?.name || 'this app'} from the tech stack?`)) return;
    const updatedUserTechStack = userTechStack.filter(item => item.toolId !== appModal.toolId);
    setAppSaving(true);
    try {
      await updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);
      setUserTechStack(updatedUserTechStack);
      await fetchTechStackData(updatedUserTechStack);
      setAppModal(null);
    } catch (error) {
      console.error('Error removing added app:', error);
      alert('Could not remove that app. Please try again.');
    } finally {
      setAppSaving(false);
    }
  };

  const handleToolClick = (e, toolId, categoryId) => {
    e.stopPropagation();
    // Apps added with the tier "+" open their own editor, not the template picker
    const clicked = techStack[categoryId]?.tools.find(tool => tool.id === toolId);
    if (clicked?.addedApp) {
      const entry = userTechStack.find(item => item.toolId === toolId);
      setAppModal({
        mode: 'edit',
        categoryId,
        toolId,
        initial: {
          name: entry?.name || clicked.software,
          toolName: entry?.toolName || clicked.name,
          baseToolId: entry?.baseToolId || null,
          open: !!entry?.open,
          libraryName: entry && !entry.isCustom ? entry.name : null,
        },
      });
      return;
    }
    setActiveTool(toolId === activeTool ? null : toolId);
    setActiveLayer(categoryId);
    
    // Restore custom software details if the saved selection is custom
    if (toolId !== activeTool) {
      const toolData = techStack[categoryId].tools.find(tool => tool.id === toolId);
      if (toolData && toolData.software && toolData.software !== 'None') {
        // Check if it's custom software by looking in userTechStack first
        const customItem = userTechStack.find(item =>
          item.categoryId === categoryId &&
          item.toolId === toolId &&
          item.isCustom
        );

        if (customItem) {
          // It's custom software - restore from userTechStack
          setCustomSoftware({
            name: customItem.name || '',
            open: customItem.open || false
          });
        } else {
          // Check if it's in availableSoftware
          const selectedSoftware = toolData.availableSoftware?.find(sw => sw.name === toolData.software);
          if (!selectedSoftware) {
            // Not in availableSoftware and not in userTechStack - might be a new custom entry
            setCustomSoftware({
              name: toolData.software === '__CUSTOM__' ? '' : toolData.software,
              open: false
            });
          } else {
            // Regular software
            setCustomSoftware({ name: '', open: false });
          }
        }
      } else {
        // Reset selections for tools with no software or "None" selected
        setCustomSoftware({ name: '', open: false });
      }
    }
  };

  const handleClosePopup = async () => {
    // Save the current software choice before closing
    if (currentUser && activeToolData) {
      const toolIndex = techStack[activeToolData.categoryId].tools.findIndex(
        tool => tool.id === activeToolData.id
      );

      if (toolIndex !== -1 && (activeToolData.software || activeToolData.software === 'None')) {
        if (activeToolData.software === 'None') {
          // Handle "None" selection
          const noneSoftwareData = {
            id: 'none',
            name: 'None',
            categoryId: activeToolData.categoryId,
            toolId: activeToolData.id,
            selectedAt: new Date().toISOString()
          };

          const updatedUserTechStack = [...userTechStack.filter(item =>
            !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
          ), noneSoftwareData];

          console.log('Saving None selection:', noneSoftwareData);
          setUserTechStack(updatedUserTechStack);
          updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);

          // Check if there was a previous software that was marked as "removed"
          const previousTool = techStack[activeToolData.categoryId].tools[toolIndex];
          if (previousTool && previousTool.software && previousTool.software !== 'None') {
            await removeFromProposedList(activeToolData.categoryId, activeToolData.id, previousTool.software, 'removed');
          }
        } else if (activeToolData.software) {
          // Check if it's custom software
          const selectedSoftware = activeToolData.availableSoftware?.find(
            sw => sw.name === activeToolData.software
          );

          // Handle custom software
          if (!selectedSoftware && customSoftware.name && customSoftware.name.trim() !== '') {
            const customSoftwareData = {
              id: `custom-${activeToolData.categoryId}-${activeToolData.id}`,
              name: customSoftware.name.trim(),
              useCaseIds: [],
              image: '',
              open: customSoftware.open,
              suggested: false,
              categoryId: activeToolData.categoryId,
              toolId: activeToolData.id,
              selectedAt: new Date().toISOString(),
              isCustom: true
            };

            const updatedUserTechStack = [...userTechStack.filter(item =>
              !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
            ), customSoftwareData];

            console.log('Saving custom software:', customSoftwareData);
            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);

            // Update tech stack
            const updatedTechStack = JSON.parse(JSON.stringify(techStack));
            updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = customSoftware.name.trim();
            setTechStack(updatedTechStack);
          } else if (selectedSoftware) {
            const selectedSoftwareData = {
              ...selectedSoftware,
              categoryId: activeToolData.categoryId,
              toolId: activeToolData.id,
              selectedAt: new Date().toISOString()
            };

            const updatedUserTechStack = [...userTechStack.filter(item =>
              !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
            ), selectedSoftwareData];

            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);

            // Remove from proposed list if it was proposed (not removed status)
            await removeFromProposedList(activeToolData.categoryId, activeToolData.id, selectedSoftware.name, 'proposed');
          }
        }
      }
    }

    setActiveTool(null);
    setCustomSoftware({ name: '', open: false });
    setShowSignupLinks(false);
  };





  // Find use cases covered by more than one selected software in the given stack
  const computeDuplicateUseCases = (stack) => {
    const useCaseMap = {};
    const duplicates = [];

    // Collect all use cases from selected software
    Object.entries(stack).forEach(([categoryId, category]) => {
      category.tools.forEach(tool => {
        if (tool.software && tool.software !== 'None') {
          const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
          if (selectedSoftware && selectedSoftware.useCaseIds) {
            selectedSoftware.useCaseIds.forEach(useCaseId => {
              const useCaseIndex = parseInt(useCaseId);
              const useCase = tool.useCases[useCaseIndex];
              if (useCase) {
                if (!useCaseMap[useCase]) {
                  useCaseMap[useCase] = [];
                }
                useCaseMap[useCase].push({
                  software: tool.software,
                  category: category.title,
                  tool: tool.name
                });
              }
            });
          }
        }
      });
    });

    // Find duplicates (use cases with multiple software)
    // Exclude "File Storage" from duplicate analysis. Core software reused on
    // another tool (e.g. Google Workspace for Scheduling) isn't a duplicate of itself.
    Object.entries(useCaseMap).forEach(([useCase, softwareList]) => {
      if (new Set(softwareList.map(sw => sw.software)).size > 1 && useCase !== 'File Storage') {
        duplicates.push({
          useCase,
          software: softwareList
        });
      }
    });

    return duplicates;
  };

  // Find the active tool's data if a popup is open
  const getActiveTool = () => {
    if (!activeTool || !activeLayer) return null;
    
    const toolData = techStack[activeLayer].tools.find(tool => tool.id === activeTool);
    return toolData ? { ...toolData, categoryId: activeLayer } : null;
  };
  
  const activeToolData = getActiveTool();

  // Select "None" for the active tool
  const handleSelectNone = async () => {
    if (!activeToolData) return;
    const updatedTechStack = JSON.parse(JSON.stringify(techStack));
    const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
      tool => tool.id === activeToolData.id
    );
    if (toolIndex === -1) return;

    const previousSoftware = updatedTechStack[activeToolData.categoryId].tools[toolIndex].software;
    updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = 'None';
    setTechStack(updatedTechStack);

    if (currentUser) {
      const noneSoftwareData = {
        id: 'none',
        name: 'None',
        categoryId: activeToolData.categoryId,
        toolId: activeToolData.id,
        selectedAt: new Date().toISOString()
      };

      const updatedUserTechStack = [...userTechStack.filter(item =>
        !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
      ), noneSoftwareData];

      setUserTechStack(updatedUserTechStack);
      updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);

      // Remove from proposed list if the previous software was marked as "removed"
      if (previousSoftware && previousSoftware !== 'None') {
        await removeFromProposedList(activeToolData.categoryId, activeToolData.id, previousSoftware, 'removed');
      }

      // "Your <Core tool>" options on other tiers follow this pick
      if (activeToolData.categoryId === 'core') await fetchTechStackData(updatedUserTechStack);
    }

    // Selection is saved - close the popup right away
    setActiveTool(null);
    setCustomSoftware({ name: '', open: false });
    setShowSignupLinks(false);
  };

  // Switch the active tool to a custom "Other" entry
  const handleSelectOther = () => {
    if (!activeToolData) return;
    const isCustomSelected = activeToolData.software &&
      activeToolData.software !== 'None' &&
      !activeToolData.availableSoftware?.find(sw => sw.name === activeToolData.software);
    if (isCustomSelected) return;

    const updatedTechStack = JSON.parse(JSON.stringify(techStack));
    const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
      tool => tool.id === activeToolData.id
    );
    if (toolIndex === -1) return;

    setCustomSoftware({ name: '', open: false });
    // Set software to a placeholder that indicates "Other" is selected
    updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = '__CUSTOM__';
    setTechStack(updatedTechStack);
  };

  // Select a software option for the active tool
  const handleSelectSoftware = async (software) => {
    if (!activeToolData) return;
    const updatedTechStack = JSON.parse(JSON.stringify(techStack));
    const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
      tool => tool.id === activeToolData.id
    );
    if (toolIndex === -1) return;

    updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = software.name;
    setTechStack(updatedTechStack);

    if (currentUser) {
      const selectedSoftwareData = {
        ...software,
        categoryId: activeToolData.categoryId,
        toolId: activeToolData.id,
        selectedAt: new Date().toISOString()
      };

      const updatedUserTechStack = [...userTechStack.filter(item =>
        !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
      ), selectedSoftwareData];

      console.log('Saving software selection:', selectedSoftwareData);
      setUserTechStack(updatedUserTechStack);
      updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);

      // Remove from proposed list if it was proposed (not removed status)
      await removeFromProposedList(activeToolData.categoryId, activeToolData.id, software.name, 'proposed');

      // "Your <Core tool>" options on other tiers follow this pick
      if (activeToolData.categoryId === 'core') await fetchTechStackData(updatedUserTechStack);
    }

    // Selection is saved - close the popup right away
    setActiveTool(null);
    setCustomSoftware({ name: '', open: false });
    setShowSignupLinks(false);

    // Warn automatically if this selection overlaps use cases with other selected software
    const duplicates = computeDuplicateUseCases(updatedTechStack).filter(duplicate =>
      duplicate.software.some(sw => sw.software === software.name && sw.tool === activeToolData.name)
    );
    if (duplicates.length > 0) {
      setDuplicateAnalysis(duplicates);
      setShowDuplicateAnalysis(true);
    }
  };

  // Tiers top to bottom, as set on the template (Core at top by default)
  const orderedCategories = tierOrder;

  // Duplicates in the current stack - drives the persistent toolbar button
  const currentDuplicates = computeDuplicateUseCases(techStack);

  return (
    <div className={embedded ? 'w-full' : 'relative w-full min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-6xl mx-auto">
        {!embedded && (
          <div className="flex justify-between items-center mb-6">
            <BackButton onClick={() => { window.location.href = '/portal/technology'; }} />
            <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center`}>Tech Stack</h1>
            <div className="w-10" />
          </div>
        )}

        {!isLoaded ? (
          <div className="flex flex-col items-center justify-center py-24">
            <BBALoader size={96} />
          </div>
        ) : (
        <>
        {/* Toggle Button and Legend */}
        <div className="flex flex-col items-center mb-4 space-y-3">
          <div className="flex space-x-3">
            {currentDuplicates.length > 0 && (
              <button
                onClick={() => {
                  setDuplicateAnalysis(currentDuplicates);
                  setShowDuplicateAnalysis(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center space-x-2"
              >
                <FaExclamationTriangle className="w-4 h-4" />
                <span>Duplicates ({currentDuplicates.length})</span>
              </button>
            )}
            {proposedTechStack.length > 0 && (
              <button 
                onClick={() => setShowProposedList(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm flex items-center space-x-2"
              >
                <FaLightbulb className="w-4 h-4" />
                <span>Proposed List ({proposedTechStack.length})</span>
              </button>
            )}
          </div>
          
          {/* API Status Legend */}
          <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-gray-100"></div>
              <span>Open API Available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative group">
                <svg 
                  className="w-4 h-4 text-blue-500 hover:text-blue-700 cursor-help transition-colors" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 ${
                  darkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-white text-gray-900 border border-gray-200'
                }`}>
                  Open API allows different software applications to communicate and share data automatically. This enables integration between your tools, reducing manual work and improving efficiency.
                  <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                    darkMode ? 'border-t-gray-800' : 'border-t-white'
                  }`}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tech Stack Pyramid */}
        <div className="flex flex-col items-center space-y-4 mb-12">
          {orderedCategories.map((categoryId) => {
            const category = techStack[categoryId];

            return (
              <div
                key={category.id}
                className="w-full flex justify-center"
              >
                <div
                  className={`${category.color} rounded-lg shadow-lg p-3`}
                  style={{ width: category.width }}
                >
                  <div className="flex justify-center items-center mb-3">
                    <h2 className="text-xl font-bold text-white text-center">{category.title}</h2>
                    <button
                      type="button"
                      onClick={() => setAppModal({ mode: 'add', categoryId: category.id })}
                      className="ml-2 w-6 h-6 rounded-full bg-white/20 hover:bg-white/35 text-white text-base font-bold leading-none flex items-center justify-center transition-colors"
                      title={`Add an app to ${category.title}`}
                      aria-label={`Add an app to ${category.title}`}
                    >
                      +
                    </button>

                  </div>
                  
                  <div className="flex flex-wrap justify-center gap-3">
                    {category.tools.map(tool => (
                      <div key={tool.id} className="flex flex-col items-center">
                        <div className="relative">
                          <div
                            className={`w-10 h-10 ${darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'} rounded-full flex items-center justify-center cursor-pointer transition-colors shadow-sm border-2 overflow-hidden hover:scale-110 active:scale-95 ${
                              tool.software && tool.software !== 'None' ? 
                                (() => {
                                  // Check if it's custom software first
                                  const isCustomSoftware = !tool.availableSoftware?.find(sw => sw.name === tool.software);
                                  
                                  if (isCustomSoftware) {
                                    // Custom software - check if it has open API (from tool object or userTechStack)
                                    const hasOpenAPI = tool.customSoftwareOpen || (() => {
                                      const customItem = userTechStack.find(item => 
                                        item.categoryId === category.id && 
                                        item.toolId === tool.id && 
                                        item.isCustom
                                      );
                                      return customItem?.open || false;
                                    })();
                                    return hasOpenAPI ? 'border-green-500' : 'border-transparent';
                                  } else if (tool.availableSoftware) {
                                    // Regular software - check in availableSoftware
                                    const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                                    return selectedSoftware?.open ? 'border-green-500' : 'border-transparent';
                                  }
                                  return 'border-transparent';
                                })() 
                                : tool.proposedSoftware ? 'border-blue-400'
                                : 'border-transparent'
                            }`}
                            onClick={(e) => handleToolClick(e, tool.id, category.id)}
                          >
                          {tool.software === 'None' ? (
                            <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-xl font-medium`}>✗</span>
                          ) : tool.software && tool.availableSoftware ? (
                            (() => {
                              const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                              const imageKey = `${tool.id}-${tool.software}`;
                              const hasImageError = imageErrors.has(imageKey);
                              
                              if (hasImageError || !selectedSoftware?.image) {
                                return (
                                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium`}>
                                    {tool.addedApp ? (tool.software || '?').charAt(0).toUpperCase() : '?'}
                                  </span>
                                );
                              }
                              
                              return (
                                <img
                                  src={selectedSoftware.image}
                                  alt={tool.software}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '';
                                    setImageErrors(prev => new Set([...prev, imageKey]));
                                  }}
                                />
                              );
                            })()
                          ) : (tool.software && tool.software !== 'None' && !tool.availableSoftware?.find(sw => sw.name === tool.software)) ? (
                            // Custom software - show "O"
                            <span className={`${darkMode ? 'text-blue-300' : 'text-blue-600'} text-xl font-bold`}>O</span>
                          ) : tool.proposedSoftware && tool.availableSoftware ? (
                            (() => {
                              const proposedSoftware = tool.availableSoftware.find(sw => sw.name === tool.proposedSoftware);
                              if (proposedSoftware?.image) {
                                return (
                                  <img
                                    src={proposedSoftware.image}
                                    alt={tool.proposedSoftware}
                                    className="w-full h-full object-cover opacity-60"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '';
                                      setImageErrors(prev => new Set([...prev, `${tool.id}-${tool.proposedSoftware}`]));
                                    }}
                                  />
                                );
                              }
                              return <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium opacity-60`}>?</span>;
                            })()
                          ) : (
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium`}>?</span>
                          )}
                          </div>
                          {(() => {
                            // Check if there's a proposed or removed item for this tool
                            const proposedItem = proposedTechStack.find(item => 
                              item.categoryId === category.id && 
                              item.toolId === tool.id
                            );
                            
                            // Only show icon if there's a proposed item and no selected software
                            if (proposedItem && !tool.software) {
                              if (proposedItem.status === 'removed') {
                                return (
                                  <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5">
                                    <FaTimes className="w-3 h-3 text-white" />
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-0.5">
                                    <FaLightbulb className="w-3 h-3 text-white" />
                                  </div>
                                );
                              }
                            }
                            // Also check tool.proposedSoftware for backward compatibility
                            if (tool.proposedSoftware && !tool.software) {
                              const removedItem = proposedTechStack.find(item => 
                                item.categoryId === category.id && 
                                item.toolId === tool.id && 
                                item.name === tool.proposedSoftware &&
                                item.status === 'removed'
                              );
                              
                              if (removedItem) {
                                return (
                                  <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5">
                                    <FaTimes className="w-3 h-3 text-white" />
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-0.5">
                                    <FaLightbulb className="w-3 h-3 text-white" />
                                  </div>
                                );
                              }
                            }
                            return null;
                          })()}
                          {/* Open API Indicator Circle */}
                          {(() => {
                            let hasOpenAPI = false;
                            
                            if (tool.software && tool.software !== 'None') {
                              // Check if it's custom software first
                              const isCustomSoftware = !tool.availableSoftware?.find(sw => sw.name === tool.software);
                                  
                              if (isCustomSoftware) {
                                // Check custom software (from tool object first, then userTechStack)
                                hasOpenAPI = tool.customSoftwareOpen || (() => {
                                  const customItem = userTechStack.find(item => 
                                    item.categoryId === category.id && 
                                    item.toolId === tool.id && 
                                    item.isCustom
                                  );
                                  return customItem?.open || false;
                                })();
                              } else if (tool.availableSoftware) {
                                // Regular software - check in availableSoftware
                                const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                                hasOpenAPI = selectedSoftware?.open || false;
                              }
                            }
                            
                            if (hasOpenAPI) {
                              return (
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" title="Open API Available"></div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <h3 className="font-medium text-center text-xs text-white mt-1">{tool.name}</h3>
                        {tool.software && (
                          <span className="text-xs text-red-100 mt-0.5">{tool.software}</span>
                        )}
                        {tool.proposedSoftware && !tool.software && (() => {
                          const removedItem = proposedTechStack.find(item => 
                            item.categoryId === category.id && 
                            item.toolId === tool.id && 
                            item.name === tool.proposedSoftware &&
                            item.status === 'removed'
                          );
                          
                          if (removedItem) {
                            return (
                              <span className="text-xs text-red-200 mt-0.5 text-center max-w-16 truncate">{tool.proposedSoftware}</span>
                            );
                          } else {
                            return (
                              <span className="text-xs text-blue-200 mt-0.5 text-center max-w-16 truncate">{tool.proposedSoftware}</span>
                            );
                          }
                        })()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
        )}
      </div>

      {/* Tool Configuration Popup */}
      {activeTool && activeToolData && (
        <motion.div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden`}
            initial={{ scale: 0.5, rotateY: 90 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <div className={`${techStack[activeToolData.categoryId].color} p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex-1"></div>
                <h2 className="text-2xl font-bold text-white text-center flex-1">{activeToolData.name}</h2>
                <button 
                  onClick={handleClosePopup}
                  className="text-white hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {(() => {
                const isCustomSelected = activeToolData.software &&
                  activeToolData.software !== 'None' &&
                  !activeToolData.availableSoftware?.find(sw => sw.name === activeToolData.software);

                return (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {/* None option */}
                      <div
                        onClick={handleSelectNone}
                        className={`border rounded-lg p-3 flex flex-col items-center text-center cursor-pointer transition-colors ${
                          activeToolData.software === 'None'
                            ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50')
                            : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                        }`}
                      >
                        <div className={`w-12 h-12 mb-2 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center`}>
                          <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-xl`}>✗</span>
                        </div>
                        <h3 className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>None</h3>
                      </div>

                      {/* Other option */}
                      <div
                        onClick={handleSelectOther}
                        className={`border rounded-lg p-3 flex flex-col items-center text-center cursor-pointer transition-colors ${
                          isCustomSelected
                            ? (darkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50')
                            : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                        }`}
                      >
                        <div className={`w-12 h-12 mb-2 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center`}>
                          <span className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} text-xl`}>+</span>
                        </div>
                        <h3 className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Other</h3>
                      </div>

                      {/* Software options */}
                      {activeToolData.availableSoftware?.map((software) => (
                        <div
                          key={software.id}
                          onClick={() => handleSelectSoftware(software)}
                          className={`relative border rounded-lg p-3 flex flex-col items-center text-center cursor-pointer transition-colors ${
                            software.name === activeToolData.software
                              ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50')
                              : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                          }`}
                        >
                          {software.suggested && (
                            <span
                              title="Suggested"
                              className="absolute top-1.5 left-1.5 text-yellow-400 text-2xl leading-none cursor-help"
                            >
                              ★
                            </span>
                          )}
                          <div className="w-12 h-12 mb-2 relative">
                            {software.image && !imageErrors.has(`popup-${software.id}`) ? (
                              <img
                                src={software.image}
                                alt={software.name}
                                className="w-full h-full object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '';
                                  setImageErrors(prev => new Set([...prev, `popup-${software.id}`]));
                                }}
                              />
                            ) : (
                              <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-xl`}>?</span>
                              </div>
                            )}
                            {software.open && (
                              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" title="Open API Available"></div>
                            )}
                          </div>
                          <h3 className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{software.name}</h3>
                          {showSignupLinks && software.signupUrl && (
                            <a
                              href={software.signupUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-blue-500 hover:text-blue-600 underline mt-1"
                            >
                              Sign Up
                            </a>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Custom Software Input Fields - Show when Other is selected */}
                    {isCustomSelected && (
                      <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} space-y-4`}>
                        <div>
                          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            Software Name *
                          </label>
                          <input
                            type="text"
                            value={customSoftware.name}
                            onChange={(e) => {
                              const newCustom = { ...customSoftware, name: e.target.value };
                              setCustomSoftware(newCustom);

                              // Update tech stack immediately
                              const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                              const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                tool => tool.id === activeToolData.id
                              );
                              if (toolIndex !== -1 && newCustom.name) {
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = newCustom.name;
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].customSoftwareOpen = newCustom.open;
                                setTechStack(updatedTechStack);
                              }
                            }}
                            placeholder="e.g., Custom CRM"
                            className={`block w-full rounded-md border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'} shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500`}
                          />
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="custom-open-api"
                            checked={customSoftware.open}
                            onChange={async (e) => {
                              const newCustom = { ...customSoftware, open: e.target.checked };
                              setCustomSoftware(newCustom);

                              // Save to user's tech stack
                              if (currentUser && customSoftware.name) {
                                const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                                const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                  tool => tool.id === activeToolData.id
                                );
                                if (toolIndex !== -1) {
                                  const customSoftwareData = {
                                    id: `custom-${activeToolData.categoryId}-${activeToolData.id}`,
                                    name: customSoftware.name,
                                    useCaseIds: [],
                                    image: '',
                                    open: e.target.checked,
                                    suggested: false,
                                    categoryId: activeToolData.categoryId,
                                    toolId: activeToolData.id,
                                    selectedAt: new Date().toISOString(),
                                    isCustom: true
                                  };

                                  // Update the tool object in tech stack to include custom software open status
                                  updatedTechStack[activeToolData.categoryId].tools[toolIndex].customSoftwareOpen = e.target.checked;

                                  const updatedUserTechStack = [...userTechStack.filter(item =>
                                    !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                  ), customSoftwareData];

                                  setUserTechStack(updatedUserTechStack);
                                  setTechStack(updatedTechStack); // Update tech stack state immediately
                                  await updateUserTechStack(effectiveUid, updatedUserTechStack, companyId || null);

                                  // Reload tech stack data to ensure everything is in sync
                                  await fetchTechStackData(updatedUserTechStack, proposedTechStack);
                                }
                              }
                            }}
                            className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}
                          />
                          <label htmlFor="custom-open-api" className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Has Open API
                          </label>
                        </div>

                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-2`}>
                          * Required fields
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t`}>
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setShowSignupLinks(!showSignupLinks)}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                    showSignupLinks
                      ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                      : (darkMode ? 'border-gray-500 text-gray-300 hover:bg-gray-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100')
                  }`}
                >
                  {showSignupLinks ? 'Hide Sign Up Links' : 'Show Sign Up Links'}
                </button>
                <button
                  onClick={handleClosePopup}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Save & Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Tier "+" — add any app / edit an added one */}
      {appModal && (
        <AddTechStackAppModal
          mode={appModal.mode}
          tierTitle={techStack[appModal.categoryId]?.title || ''}
          initial={appModal.initial}
          toolTypes={tierToolTypes(techStack[appModal.categoryId])}
          saving={appSaving}
          onClose={() => !appSaving && setAppModal(null)}
          onSave={saveAddedApp}
          onRemove={removeAddedApp}
        />
      )}

      {/* Duplicate Analysis Modal */}
      {showDuplicateAnalysis && (
        <motion.div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden`}
            initial={{ scale: 0.5, rotateY: 90 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <div className="bg-blue-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1"></div>
                <h2 className="text-2xl font-bold text-white text-center flex-1">Duplicate Use Case Analysis</h2>
                <button 
                  onClick={() => setShowDuplicateAnalysis(false)}
                  className="text-white hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {duplicateAnalysis.length > 0 ? (
                <div className="space-y-6">
                  <div className={`text-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-lg`}>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-blue-800'}`}>
                      Found {duplicateAnalysis.length} use case(s) with multiple software solutions. Consider removing redundant software.
                    </p>
                  </div>

                  {duplicateAnalysis.map((duplicate, index) => (
                    <div key={index} className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4`}>
                      <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                        {duplicate.useCase}
                      </h3>
                      <div className="space-y-3">
                        {duplicate.software.map((sw, swIndex) => (
                          <div key={swIndex} className={`bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700`}>
                            <div>
                              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{sw.software}</h4>
                              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {sw.category} • {sw.tool}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center p-8 ${darkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-lg`}>
                  <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-green-800'} mb-2`}>
                    No Duplicates Found! 🎉
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-green-700'}`}>
                    Your tech stack is well-optimized with no overlapping use cases detected.
                  </p>
                </div>
              )}
            </div>
            
            <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t`}>
              <div className="flex justify-end">
                <button 
                  onClick={() => setShowDuplicateAnalysis(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Proposed List Modal */}
      {showProposedList && (
        <motion.div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden`}
            initial={{ scale: 0.5, rotateY: 90 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <div className="bg-green-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1"></div>
                <h2 className="text-2xl font-bold text-white text-center flex-1">Proposed Tech Stack List</h2>
                <button 
                  onClick={() => setShowProposedList(false)}
                  className="text-white hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex justify-center space-x-4 mt-2">
                <span className="text-white text-sm">
                  Proposed: {proposedTechStack.filter(item => item.status !== 'removed').length}
                </span>
                <span className="text-white text-sm">
                  Removed: {proposedTechStack.filter(item => item.status === 'removed').length}
                </span>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {proposedTechStack.length === 0 ? (
                <div className="text-center py-8">
                  <FaLightbulb className={`text-4xl ${darkMode ? 'text-gray-400' : 'text-gray-400'} mx-auto mb-4`} />
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    No proposed software yet. Use the "Propose" button to add items.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {proposedTechStack
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((item, index) => {
                      const tool = techStack[item.categoryId]?.tools?.find(t => t.id === item.toolId);
                      const categoryName = item.categoryId ? item.categoryId.charAt(0).toUpperCase() + item.categoryId.slice(1) : '';
                      const isRemoved = item.status === 'removed';
                      
                      return (
                        <div
                          key={`${item.categoryId}-${item.toolId}-${index}`}
                          className={`border rounded-lg p-4 ${
                            isRemoved 
                              ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50')
                              : (darkMode ? 'border-yellow-500 bg-yellow-900/20' : 'border-yellow-500 bg-yellow-50')
                          }`}
                        >
                          <div className="flex items-start space-x-4">
                            {/* Software Info */}
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                {item.image && (
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded-lg"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                      {item.name}
                                    </h3>
                                    {isRemoved ? (
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
                                      }`}>
                                        Removed
                                      </span>
                                    ) : (
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        Proposed
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {tool?.name || 'Unknown Tool'} • {categoryName}
                                  </p>
                                </div>
                              </div>

                              {/* Description */}
                              {item.description && (
                                <div className="mt-3">
                                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {item.description}
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            {/* Dismiss Button */}
                            <button
                              onClick={() => handleDismissProposedItem(item)}
                              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                darkMode 
                                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white' 
                                  : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900'
                              }`}
                              title="Dismiss"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
            
            <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t`}>
              <div className="flex justify-end">
                <button 
                  onClick={() => setShowProposedList(false)}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default TechStackVisualizer; 